// Play alone's way in (plain TS, so it is tested on its own): the server's round when the phone is
// online, which the server decides and pays; the on-phone round when there is no network, which pays
// nothing and says so. Any other refusal (a game type switched off, the leave rules' wait, a seat in
// another park) is said on the menu: the on-phone round is for no network only, never a way round them.
import type { PlayerView } from '@wahala/simulation';
import { ApiError, type JoinTicket } from '../lib/api';

export interface PlayAloneDoors {
  /** The phone thinks it is online (navigator.onLine). */
  online: () => boolean;
  /** Ask the api for a solo park. */
  server: () => Promise<JoinTicket>;
  /** Into the server's park with its ticket. */
  enter: (t: JoinTicket) => void;
  /** The on-phone round (no server, no pay), with no network. */
  local: () => void;
  /** The api said no: say why on the menu. */
  refuse: (e: unknown) => void;
}

export async function playAlone(d: PlayAloneDoors): Promise<'server' | 'local' | 'refused'> {
  if (!d.online()) {
    d.local();
    return 'local';
  }
  let t: JoinTicket;
  try {
    t = await d.server();
  } catch (e) {
    // only a network failure falls back to the phone: the api's own answers stand
    if (e instanceof ApiError && e.code === 'network') {
      d.local();
      return 'local';
    }
    d.refuse(e);
    return 'refused';
  }
  d.enter(t);
  return 'server';
}

/**
 * The step a finished round moves the player's Play alone level, as the server moves its solo
 * drivers: a race up for first over the line, down for the back half; otherwise up for a win, down
 * for a loss. Null while the round is not over.
 */
export function soloStep(v: Pick<PlayerView, 'winner' | 'race' | 'you'>): number | null {
  if (!v.winner) return null;
  if (v.race) {
    const { place, of } = v.race;
    return place === 1 ? 1 : place - 1 >= Math.ceil(of / 2) ? -1 : 0;
  }
  const mine = v.you.role === 'impostor' ? 'impostor' : 'crew';
  return v.winner.team === mine ? 1 : -1;
}
